import { useState, useEffect, useRef, useCallback } from 'react';
import API, { authFetch } from './api';

// Poll fast while something is actually scoring, slowly when idle. The pending state clears
// itself: a target drops out of the map as soon as its job reaches 'done'.
const ACTIVE_MS = 3000;
const IDLE_MS = 15000;

const EMPTY = { buyers: {}, properties: {}, failed: [], scoring_count: 0, failed_count: 0 };

/**
 * Live background-scoring state for everything the current user can see.
 *
 * @param {object}   opts
 * @param {boolean}  opts.enabled    set false to stop polling (e.g. logged out)
 * @param {Function} opts.onSettled  called once when the last in-flight job finishes —
 *                                   use it to reload scores/boards so the numbers appear
 *                                   without the user refreshing.
 */
export default function useScoringStatus({ enabled = true, onSettled } = {}) {
  const [status, setStatus] = useState(EMPTY);
  const alive = useRef(true);
  const timer = useRef(null);
  const wasScoring = useRef(false);
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/scoring/status`);
      if (!res.ok) return null;
      const data = await res.json();
      if (!alive.current) return null;
      setStatus(data);
      // Transition from "something scoring" to "nothing scoring" => the work landed.
      if (wasScoring.current && data.scoring_count === 0) {
        if (settledRef.current) settledRef.current();
      }
      wasScoring.current = data.scoring_count > 0;
      return data;
    } catch {
      return null; // transient network error — keep last known state and retry next tick
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return () => { alive.current = false; };

    const tick = async () => {
      const data = await load();
      if (!alive.current) return;
      timer.current = setTimeout(tick, data && data.scoring_count > 0 ? ACTIVE_MS : IDLE_MS);
    };
    tick();

    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [enabled, load]);

  const buyerState = useCallback((id) => status.buyers[String(id)] || null, [status]);
  const propertyState = useCallback((id) => status.properties[String(id)] || null, [status]);
  const failedJob = useCallback(
    (kind, id) => status.failed.find((f) => f.kind === kind && String(f.target_id) === String(id)) || null,
    [status]
  );

  const retry = useCallback(async (jobId) => {
    try {
      await authFetch(`${API}/api/scoring/retry`, {
        method: 'POST',
        body: JSON.stringify({ job_id: jobId }),
      });
    } catch {
      /* surfaced on the next poll */
    }
    await load();
  }, [load]);

  return { status, buyerState, propertyState, failedJob, retry, refresh: load };
}
